import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, Divider, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsIcon from "@mui/icons-material/Directions";
import MapIcon from "@mui/icons-material/Map";
import moment from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { toast } from "react-toastify";

const LIVE_WINDOW_MINUTES = 3;
const ON_SITE_METERS = 150;
const METERS_PER_MILE = 1609.344;

const MOTION_COLOR = {
  moving: "success",
  idle: "warning",
  parked: "default",
  unknown: "default",
};

// 0 is a valid reading, so only null/undefined/""/NaN are treated as missing.
export const toInteger = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : null;
};

const formatInteger = (value, unit = "") => {
  const number = toInteger(value);
  if (number == null) return "--";
  return `${number.toLocaleString()}${unit ? ` ${unit}` : ""}`;
};

const titleCase = (value) =>
  value ? String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "--";

function distanceMeters(from, to) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(a));
}

// Re-renders on an interval so "x min ago" and Live/Stale stay current between telemetry messages.
function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

function Section({ title, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {title}
      </MDTypography>
      <MDBox mt={0.75} display="flex" flexDirection="column" gap={0.5}>
        {children}
      </MDBox>
    </MDBox>
  );
}

function Row({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
      <MDTypography variant="button" color="text" fontWeight="regular">
        {label}
      </MDTypography>
      <MDTypography variant="button" color="text" fontWeight="bold" textAlign="right">
        {value ?? "--"}
      </MDTypography>
    </MDBox>
  );
}

const chipSx = { height: 24, borderRadius: "6px", fontWeight: 700 };

export default function TabletMapDetailsPanel({ details = {}, site, sitePosition, position }) {
  const now = useNow();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const lastUpdated = details.lastUpdated ? moment(details.lastUpdated) : null;
  const isLive = Boolean(
    lastUpdated?.isValid() &&
      lastUpdated.isAfter(moment(now).subtract(LIVE_WINDOW_MINUTES, "minutes"))
  );
  const freshnessLabel = lastUpdated?.isValid() ? lastUpdated.from(moment(now)) : "No data yet";

  const motion = String(details.motion || "unknown").toLowerCase();
  const alertCount = Number(details.activeAlertCount) || 0;
  const faultCount = Number(details.activeFaultCount) || 0;

  const siteDistance = position && sitePosition ? distanceMeters(position, sitePosition) : null;
  const siteStatus =
    siteDistance == null
      ? null
      : siteDistance <= ON_SITE_METERS
        ? "On site"
        : `${(siteDistance / METERS_PER_MILE).toFixed(1)} mi from site`;

  const coordinateText = position ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : "";

  const handleCopy = async () => {
    if (!coordinateText || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(coordinateText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("copy coordinates", error);
    }
  };

  const handleOpenMaps = () => {
    if (!position) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <MDBox
      width={{ xs: "100%", md: 340 }}
      flexShrink={0}
      p={2.5}
      sx={({ palette, functions: { rgba } }) => ({
        overflowY: "auto",
        borderLeft: { md: `1px solid ${rgba(palette.text.main, 0.12)}` },
        borderTop: { xs: `1px solid ${rgba(palette.text.main, 0.12)}`, md: "none" },
      })}
    >
      <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
        <Chip size="small" sx={chipSx} label={isLive ? "LIVE" : "STALE"} color={isLive ? "success" : "error"} />
        <Chip size="small" sx={chipSx} label={titleCase(motion)} color={MOTION_COLOR[motion] ?? "default"} />
        {details.ptoActive && <Chip size="small" sx={chipSx} label="PTO Drive" color="success" />}
        {alertCount > 0 && (
          <Chip size="small" sx={chipSx} label={`${alertCount} alert${alertCount === 1 ? "" : "s"}`} color="warning" />
        )}
      </MDBox>

      <Section title="Status">
        <Row label="Last update" value={freshnessLabel} />
        <Row label="Speed" value={formatInteger(details.speed, "mph")} />
      </Section>

      <Section title="Tank">
        <Row label="Level" value={formatInteger(details.levelPercent, "%")} />
        <Row label="Volume" value={formatInteger(details.volumeGallons, "gal")} />
        <Row label="Capacity" value={formatInteger(details.capacityGallons, "gal")} />
        <Row label="Remaining until full" value={formatInteger(details.gallonsRemaining, "gal")} />
      </Section>

      <Section title="Pressure">
        <Row label="PSI" value={formatInteger(details.pressurePsi, "psi")} />
        <Row label="Mode" value={titleCase(details.pressureMode)} />
      </Section>

      <Section title="Operations">
        <Row label="Job state" value={titleCase(details.jobState)} />
        <Row
          label="Transfer"
          value={
            details.transferActive
              ? `Active${details.transferStartedAt ? ` · ${moment(details.transferStartedAt).from(moment(now))}` : ""}`
              : "Inactive"
          }
        />
      </Section>

      <Section title="Health">
        <Row label="Active alerts" value={alertCount} />
        <Row label="Vehicle faults" value={faultCount} />
      </Section>

      {site && (
        <Section title="Assigned site">
          <Row label="Name" value={site.name || "--"} />
          {site.address && (
            <MDTypography variant="caption" color="text">
              {site.address}
            </MDTypography>
          )}
          {siteStatus && (
            <MDBox mt={0.5}>
              <Chip
                size="small" sx={chipSx}
                label={siteStatus}
                color={siteStatus === "On site" ? "success" : "default"}
              />
            </MDBox>
          )}
          <MDBox mt={1}>
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              startIcon={<MapIcon />}
              onClick={() => {
                const addressItemId = site.address_item_id;
                if (addressItemId) {
                  navigate(`/geofence-management/new?address_item_id=${addressItemId}`);
                } else {
                  toast.info("No address item associated with this site.");
                }
              }}
            >
              Link Geofence
            </MDButton>
          </MDBox>
        </Section>
      )}

      <Divider sx={{ my: 2 }} />

      <Section title="Location">
        <Row label="Coordinates" value={coordinateText || "--"} />
        <MDBox display="flex" gap={1} mt={1}>
          <Tooltip title={copied ? "Copied" : "Copy coordinates"}>
            <span>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                disabled={!coordinateText}
              >
                {copied ? "Copied" : "Copy"}
              </MDButton>
            </span>
          </Tooltip>
          <MDButton
            size="small"
            variant="gradient"
            color="info"
            startIcon={<DirectionsIcon />}
            onClick={handleOpenMaps}
            disabled={!position}
          >
            Google Maps
          </MDButton>
        </MDBox>
      </Section>
    </MDBox>
  );
}
